import { groupBy } from "lodash";

import { resultKey } from "src/components/commandbar/rows";
import {
  type CommandResult,
  type CommandResultType,
  commandResultTypeLabel,
} from "src/components/commandbar/types";
import { formatRelativeShort } from "src/utils/time";

// A zero timestamp is an unset one (seeded and migrated rows carry 1970), so it labels nothing.
const timeLabel = (result: CommandResult) =>
  result.updatedAt && Date.parse(result.updatedAt) > 0
    ? `${result.isEditedLabelled ? "Edited" : "Updated"} ${formatRelativeShort(result.updatedAt)}`
    : undefined;

// Most telling first: a type-specific detail, then when the row last changed. Never an id, which reads as noise.
const candidates = (result: CommandResult): Array<string | undefined> => [
  result.detail,
  timeLabel(result),
];

const nameKey = (result: CommandResult) => result.name.trim().toLowerCase();

// Rows of one type that share a name get the first candidate that differs for every one of them, or else their change time even if it matches.
// Rows of different types that share a name lead with their type. Unique names get nothing.
export const disambiguate = (
  results: CommandResult[],
  typeLabel: (type: CommandResultType) => string = commandResultTypeLabel,
): Map<string, string> => {
  const labels = new Map<string, string>();
  const groups = groupBy(
    results,
    (result) => `${result.type}:${nameKey(result)}`,
  );
  for (const group of Object.values(groups)) {
    if (group.length < 2) {
      continue;
    }
    const options = group.map(candidates);
    const index = options[0]?.findIndex((_, i) => {
      const values = options.map((option) => option[i]);
      return values.every(Boolean) && new Set(values).size === values.length;
    });
    const chosen = index === undefined || index === -1 ? 1 : index;
    group.forEach((result, i) => {
      const label = options[i]?.[chosen];
      if (label) {
        labels.set(resultKey(result), label);
      }
    });
  }
  for (const group of Object.values(groupBy(results, nameKey))) {
    if (new Set(group.map((result) => result.type)).size < 2) {
      continue;
    }
    for (const result of group) {
      const key = resultKey(result);
      const label = labels.get(key);
      const type = typeLabel(result.type);
      labels.set(key, label ? `${type} · ${label}` : type);
    }
  }
  return labels;
};
